import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { StatusBadge } from "@/components/StatusBadge";
import { ProcessingFeeCard } from "@/components/ProcessingFeeCard";
import { MyLoans } from "@/components/MyLoans";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { formatINR, formatDateTime, formatDate } from "@/lib/format";
import {
  LogOut, ArrowRight, Loader2, Wallet, Clock, FileText, CheckCircle2,
  Info, ReceiptIndianRupee, User2, Ban, CalendarClock, MessageCircle,
} from "lucide-react";
import { motion } from "framer-motion";

const Stat = ({ label, value, mono }) => (
  <div className="flex items-center justify-between py-2 border-b border-border/60 last:border-0">
    <span className="text-sm text-muted-foreground">{label}</span>
    <span className={`text-sm font-semibold ${mono ? "font-mono" : ""}`}>{value}</span>
  </div>
);

// Live clock anchored to server time (device time is not trusted).
function useServerNow(serverNowIso) {
  const offsetRef = useRef(0);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (serverNowIso) offsetRef.current = new Date(serverNowIso).getTime() - Date.now();
    setNow(Date.now() + offsetRef.current);
    const t = setInterval(() => setNow(Date.now() + offsetRef.current), 1000);
    return () => clearInterval(t);
  }, [serverNowIso]);
  return now;
}

const RejectedCard = ({ app, serverNow, onApply }) => {
  const now = useServerNow(serverNow);
  const unlockMs = app.reapply_available_at ? new Date(app.reapply_available_at).getTime() : null;
  const remaining = unlockMs ? unlockMs - now : 0;
  const locked = remaining > 0;
  const days = Math.max(0, Math.floor(remaining / 86400000));
  const hours = Math.max(0, Math.floor((remaining % 86400000) / 3600000));
  const mins = Math.max(0, Math.floor((remaining % 3600000) / 60000));
  const secs = Math.max(0, Math.floor((remaining % 60000) / 1000));

  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
      <Card className="p-6 sm:p-8 ip-card-shadow text-center" data-testid="application-rejected-card">
        <div className="mx-auto h-14 w-14 grid place-items-center rounded-full bg-rose-50 text-rose-600 mb-4">
          <Ban className="h-7 w-7" />
        </div>
        <h1 className="text-2xl font-extrabold tracking-tight uppercase" data-testid="rejected-heading">Not Eligible</h1>
        <p className="text-muted-foreground mt-2 max-w-md mx-auto">Your loan application is not eligible at this time.</p>
        <p className="text-muted-foreground text-sm">You can try again after 7 days.</p>

        <div className="mt-6 max-w-sm mx-auto text-left bg-slate-50 rounded-xl border border-border p-4 space-y-1">
          <Stat label="Application Reference" value={app.reference_number} mono />
          <Stat label="Rejected Date" value={formatDateTime(app.rejected_at)} />
          <Stat label="Re-apply Available" value={formatDateTime(app.reapply_available_at)} />
        </div>

        <div className="mt-6">
          {locked ? (
            <>
              <div className="inline-flex flex-wrap items-center justify-center gap-x-2 gap-y-1 rounded-full bg-amber-50 border border-amber-200 px-4 py-2 text-sm text-amber-800 font-semibold text-center max-w-full" data-testid="reapply-countdown">
                <Clock className="h-4 w-4 shrink-0" />
                <span>Try again in {days}d {hours}h {mins}m {secs}s</span>
              </div>
              <div>
                <Button disabled className="mt-4 h-11 opacity-60 cursor-not-allowed" data-testid="reapply-button">
                  <FileText className="h-4 w-4 mr-2" /> Apply for Loan
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-2">The Apply button unlocks automatically when your 7-day waiting period ends.</p>
            </>
          ) : (
            <>
              <div className="inline-flex items-center gap-2 rounded-full bg-emerald-50 border border-emerald-200 px-4 py-2 text-sm text-emerald-700 font-semibold" data-testid="reapply-available">
                <CalendarClock className="h-4 w-4" /> You can apply again now
              </div>
              <div>
                <Button onClick={onApply} className="mt-4 h-11 bg-gold hover:bg-gold-dark text-white" data-testid="reapply-button">
                  Apply for Loan <ArrowRight className="h-4 w-4 ml-1.5" />
                </Button>
              </div>
            </>
          )}
        </div>
      </Card>
    </motion.div>
  );
};

export default function CustomerDashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const res = await api.get("/dashboard");
      setData(res.data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  if (loading)
    return <div className="min-h-screen grid place-items-center"><Loader2 className="h-8 w-8 animate-spin text-brand" /></div>;

  const app = data?.application;
  const offer = data?.offer;

  const noActive = !app;
  const showOffer = app && app.status === "APPROVED" && offer && offer.status === "active";
  const disbursePending = app && app.status === "DISBURSE_PENDING";
  const disbursed = app && app.status === "DISBURSED" && offer;

  return (
    <div className="min-h-screen bg-background pb-16">
      <header className="sticky top-0 z-40 backdrop-blur-md bg-white/90 border-b border-border">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Logo />
          <div className="flex items-center gap-3">
            <span className="hidden sm:inline text-sm text-muted-foreground">Hi, <b className="text-foreground">{user?.name}</b></span>
            <Button variant="outline" size="sm" onClick={() => nav("/support")} data-testid="header-support-button">
              <MessageCircle className="h-4 w-4 mr-1.5" /> Support
            </Button>
            <Button variant="outline" size="sm" onClick={async () => { await logout(); nav("/login"); }} data-testid="logout-button">
              <LogOut className="h-4 w-4 mr-1.5" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        {/* Hero / eligible range — only before any application exists */}
        {noActive && (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="ip-gradient rounded-2xl p-6 sm:p-8 text-white ip-card-shadow">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-white/70">Loan amount range</p>
            <div className="mt-2 flex items-baseline gap-2 flex-wrap">
              <span className="font-mono text-3xl sm:text-4xl font-extrabold tracking-tight">{formatINR(1000)}</span>
              <span className="text-white/60 text-xl">to</span>
              <span className="font-mono text-3xl sm:text-4xl font-extrabold tracking-tight">{formatINR(100000)}</span>
            </div>
            <p className="mt-3 text-sm text-white/80 max-w-lg">Final loan amount and terms are reviewed and set by our team. You'll only see an offer once it is approved.</p>
            <Button onClick={() => nav("/apply")} className="mt-5 bg-gold hover:bg-gold-dark text-white h-11" data-testid="apply-now-hero-button">
              Apply Now <ArrowRight className="h-4 w-4 ml-1.5" />
            </Button>
          </motion.div>
        )}

        {/* Application Under Review — the ONLY view before admin approval */}
        {app && (app.status === "UNDER_REVIEW" || app.status === "INFO_REQUESTED") && (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
            <Card className="p-6 sm:p-8 ip-card-shadow text-center" data-testid="application-under-review-card">
              <div className="mx-auto h-14 w-14 grid place-items-center rounded-full bg-sky-50 text-sky-600 mb-4">
                <Clock className="h-7 w-7" />
              </div>
              <h1 className="text-2xl font-extrabold tracking-tight uppercase" data-testid="under-review-heading">Application Under Review</h1>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground mt-4">Reference Number</p>
              <p className="font-mono text-xl font-bold mt-1" data-testid="under-review-reference">{app.reference_number}</p>
              <p className="text-muted-foreground mt-4 max-w-md mx-auto">
                Your application has been submitted successfully and is currently under review.
              </p>
              <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-sky-50 border border-sky-200 px-4 py-2 text-sm text-sky-800 font-medium">
                <Clock className="h-4 w-4" /> Estimated review time: 1–30 minutes.
              </div>
              {app.status === "INFO_REQUESTED" && (
                <div className="mt-4 flex gap-2 rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800 text-left">
                  <Info className="h-4 w-4 shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold">More information requested</p>
                    <p>{app.info_request}</p>
                  </div>
                </div>
              )}
            </Card>
          </motion.div>
        )}

        {/* Not Eligible — 7-day re-apply lock after admin rejection */}
        {app && ["REJECTED", "CANCELLED"].includes(app.status) && (
          <RejectedCard app={app} serverNow={data?.server_now} onApply={() => nav("/apply")} />
        )}

        {/* Application status — approved / disbursed states */}
        {app && !["UNDER_REVIEW", "INFO_REQUESTED", "REJECTED", "CANCELLED"].includes(app.status) && (
          <Card className="p-5 sm:p-6 ip-card-shadow" data-testid="application-status-card">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">Current Application</p>
                <p className="font-mono text-lg font-bold mt-1" data-testid="application-reference-number">{app.reference_number}</p>
                <p className="text-xs text-muted-foreground mt-0.5">Submitted {formatDateTime(app.submitted_at)}</p>
              </div>
              <StatusBadge status={app.status} />
            </div>
          </Card>
        )}

        {/* Eligible offer (only when approved & active) */}
        {showOffer && (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
            <Card className="p-5 sm:p-6 border-2 border-emerald-300 ip-card-shadow" data-testid="eligible-offer-card">
              <div className="flex items-center gap-2 text-emerald-700">
                <Wallet className="h-5 w-5" />
                <p className="text-xs font-semibold uppercase tracking-[0.15em]">Your Eligible Loan Amount</p>
              </div>
              <p className="font-mono text-4xl font-extrabold text-brand mt-2" data-testid="eligible-amount">{formatINR(offer.approved_amount)}</p>
              <div className="mt-4 grid sm:grid-cols-2 gap-x-8">
                <Stat label="Disbursement amount" value={formatINR(offer.disbursement_amount)} mono />
                <Stat label="Processing fee" value={formatINR(offer.processing_fee)} mono />
                <Stat label="GST" value={formatINR(offer.gst)} mono />
                <Stat label="Interest" value={`${offer.interest_rate}%`} mono />
                <Stat label="Total repayment" value={formatINR(offer.repayment_amount)} mono />
                <Stat label="Tenure" value={`${offer.tenure} ${offer.tenure_unit}`} />
                <Stat label="Repayment date" value={formatDate(offer.repayment_date)} />
              </div>
              <Button onClick={() => nav("/offer")} className="mt-5 w-full sm:w-auto bg-gold hover:bg-gold-dark text-white h-11" data-testid="offer-apply-now-button">
                Apply Now <ArrowRight className="h-4 w-4 ml-1.5" />
              </Button>
            </Card>
          </motion.div>
        )}

        {/* Processing fee payment (Cashfree) — amount from the admin's active offer */}
        {app && !["UNDER_REVIEW", "INFO_REQUESTED", "REJECTED", "CANCELLED"].includes(app.status) && (
          <ProcessingFeeCard payment={data?.payment} onDone={load} />
        )}

        {/* Disbursement processing */}
        {disbursePending && (
          <Card className="p-5 sm:p-6 ip-card-shadow" data-testid="disbursement-processing-card">
            <div className="flex gap-3">
              <div className="h-10 w-10 grid place-items-center rounded-full bg-indigo-50 text-indigo-600 shrink-0"><Loader2 className="h-5 w-5 animate-spin" /></div>
              <div>
                <p className="font-semibold">Disbursement processing</p>
                <p className="text-sm text-muted-foreground mt-0.5">Your loan disbursement is being processed. Estimated processing time: 1 minute to 1 hour.</p>
              </div>
            </div>
          </Card>
        )}

        {/* Repayment (after disbursed) */}
        {disbursed && (
          <Card className="p-5 sm:p-6 ip-card-shadow" data-testid="repayment-card">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-2 text-brand">
                <ReceiptIndianRupee className="h-5 w-5" />
                <p className="text-xs font-semibold uppercase tracking-[0.15em]">Active Loan — Repayment</p>
              </div>
              <StatusBadge status={offer.status} />
            </div>
            <div className="mt-4 grid sm:grid-cols-2 gap-x-8">
              <Stat label="Disbursed amount" value={formatINR(offer.disbursement_amount)} mono />
              <Stat label="Total repayment" value={formatINR(offer.repayment_amount)} mono />
              <Stat label="Amount paid" value={formatINR(offer.amount_paid)} mono />
              <Stat label="Outstanding" value={formatINR(offer.outstanding)} mono />
              <Stat label="Interest" value={`${offer.interest_rate}%`} mono />
              <Stat label="Processing fee" value={formatINR(offer.processing_fee)} mono />
              <Stat label="GST" value={formatINR(offer.gst)} mono />
              <Stat label="Late penalty" value={formatINR(offer.late_penalty)} mono />
              <Stat label="Tenure" value={`${offer.tenure} ${offer.tenure_unit}`} />
              <Stat label="Repayment due date" value={formatDate(offer.repayment_date)} />
            </div>
            {offer.outstanding > 0 ? (
              <Button onClick={() => nav("/repay")} className="mt-5 w-full sm:w-auto h-11" data-testid="repay-now-button">
                Repay Now <ArrowRight className="h-4 w-4 ml-1.5" />
              </Button>
            ) : (
              <div className="mt-5 inline-flex items-center gap-2 text-emerald-700 font-semibold text-sm"><CheckCircle2 className="h-5 w-5" /> Loan fully repaid</div>
            )}
          </Card>
        )}

        {/* Application History — every application submitted by this customer */}
        {(data?.applications || []).length > 0 && (
          <Card className="p-5 sm:p-6 ip-card-shadow" data-testid="application-history-card">
            <div className="flex items-center gap-2 mb-3">
              <FileText className="h-5 w-5 text-muted-foreground" />
              <p className="font-semibold">Application History</p>
            </div>
            <div className="space-y-2">
              {data.applications.map((a) => (
                <div key={a.id} className="flex items-center justify-between rounded-lg border border-border px-3 py-2.5" data-testid={`application-history-${a.reference_number}`}>
                  <div>
                    <p className="font-mono font-semibold text-sm">{a.reference_number}</p>
                    <p className="text-xs text-muted-foreground">Submitted {formatDateTime(a.submitted_at)}</p>
                  </div>
                  <StatusBadge status={a.status} />
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* My Loans — all separate loans assigned to this customer */}
        <MyLoans />

        {/* Chat with Support */}
        <Card className="p-5 sm:p-6 ip-card-shadow" data-testid="support-chat-card">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="h-11 w-11 grid place-items-center rounded-full bg-brand-light text-brand shrink-0"><MessageCircle className="h-5 w-5" /></div>
              <div>
                <p className="font-semibold">Chat with Support</p>
                <p className="text-sm text-muted-foreground">Questions about your loan? Message our team — we usually reply fast.</p>
              </div>
            </div>
            <Button onClick={() => nav("/support")} className="h-11 bg-brand hover:bg-brand/90 text-white" data-testid="open-support-chat-button">
              Open Chat <ArrowRight className="h-4 w-4 ml-1.5" />
            </Button>
          </div>
        </Card>

        {/* Profile */}
        <Card className="p-5 sm:p-6 ip-card-shadow" data-testid="profile-card">
          <div className="flex items-center gap-2 mb-3">
            <User2 className="h-5 w-5 text-muted-foreground" />
            <p className="font-semibold">Profile</p>
          </div>
          <Stat label="Name" value={user?.name} />
          <Stat label="Email" value={user?.email} />
          <Stat label="Phone" value={user?.phone} mono />
        </Card>
      </main>
    </div>
  );
}
